export * from "./GetPricingInteractor";
export * from "./PricingDataRepository";
export * from "./PricingPresenter";
export * from "./usePricingController";

interface IEnterpriseProduct {
  price: number;
  priceId: {
    test: string;
    prod: string;
  };
  included: string[];
  length: number;
}

interface IEnterpriseProductsMap {
  copper:  IEnterpriseProduct;
  bronze: IEnterpriseProduct;
  silver: IEnterpriseProduct;
  gold: IEnterpriseProduct;
  platinum: IEnterpriseProduct;
}

export type EnterpriseTier = keyof IEnterpriseProductsMap;

export const EnterpriseProductsMap: IEnterpriseProductsMap = {
  copper: {
    price: 500,
    priceId: {
        test: "price_1Sb3QfP7Ih4BDoKIB4sVeudY",
        prod: "price_1Sb3ZvP7Ih4BDoKIeR3ghuzC"
    },
    included: [ "Access to Client Management Dashboard", "Closed referral loop between you and ClientVitals" ],
    length: 12
  },
  bronze: {
    price: 9900,
    priceId: {
        test: "price_1Sb3RSP7Ih4BDoKIxJt5c1mO",
        prod: "price_1Sb3ZxP7Ih4BDoKIdBpzUZ7y"
    },
    included: [ "Access to Client Management Dashboard", "Closed referral loop between you and ClientVitals", "Access to pull your clients' report packages ($45 per pull)" ],
    length: 12
  },
  silver: {
    price: 24900,
    priceId: {
        test: "price_1Sb3RmP7Ih4BDoKIUODCEhvs",
        prod: "price_1Sb3ZzP7Ih4BDoKIDY7iNnpG"
    },
    included: [ "Access to Client Management Dashboard", "Closed referral loop between you and ClientVitals", "Access to your clients' report packages", "5 free report packages a month ($45 per pull after 5)" ],
    length: 12
  },
  gold: {
    price: 39900,
    priceId: {
        test: "price_1Sb3S4P7Ih4BDoKILK66FTwC",
        prod: "price_1Sb3a2P7Ih4BDoKIe59Oje6f"
    },
    included: [ "Access to Client Management Dashboard", "Closed referral loop between you and ClientVitals", "Access to your clients' report packages", "10 free report packages a month ($45 per pull after 10)" ],
    length: 12
  },
  platinum: {
    price: -1,
    priceId: {
        test: "",
        prod: ""
    },
    included: [ "Access to Client Management Dashboard", "Closed referral loop between you and ClientVitals", "Access to your clients' report packages", "10 free report packages a month ($45 per pull after 10)", "Promotion on ClientVitals Website" ],
    length: -1
  }
}

export const extractEnterpriseProductPrice = (tier: EnterpriseTier) => EnterpriseProductsMap[tier].price;
export const extractEnterpriseProductInclusions = (tier: EnterpriseTier) => EnterpriseProductsMap[tier].included;
export const extractEnterpriseProductPriceId = (prod: boolean, tier: EnterpriseTier) => 
    prod ? EnterpriseProductsMap[tier].priceId.prod : EnterpriseProductsMap[tier].priceId.test;
export const isEnterpriseTier = (value: string): value is EnterpriseTier => value in EnterpriseProductsMap;

interface ICustomerProduct {
  price: number;
  priceId: {
    test: string;
    prod: string;
  };
  included: string[];
  length: number;
}

interface ICustomerProductsMap {
  bronze: ICustomerProduct;
  silver: ICustomerProduct;
  gold: ICustomerProduct;
}

export type CustomerTier = keyof ICustomerProductsMap;

export const CustomerProductsMap: ICustomerProductsMap = {
  bronze: {
    price: 500,
    priceId: {
      test: "price_1Sb3KAP7Ih4BDoKI6KKwhUUM",
      prod: "price_1Sb3ZnP7Ih4BDoKIlYuG6WSV",
    },
    included: ["Discounted Intake & Assessment", "1 Report Package Per Month"],
    length: 12,
  },
  silver: {
    price: 5000,
    priceId: {
      test: "price_1Sb3KcP7Ih4BDoKIPh6ANRuw",
      prod: "price_1Sb3ZqP7Ih4BDoKIrDiLHzdq"
    },
    included: ["Discounted Intake & Assessment", "1 Report Package Per Month", "1 Customized Action Plan per Month"],
    length: 12
  },
  gold: {
    price: 15000,
    priceId: {
      test: "price_1Sb3L3P7Ih4BDoKIsVbkWyW9",
      prod: "price_1Sb3ZtP7Ih4BDoKILAGa8lq8"
    },
    included: ["1 Report Package Per Month", "1 Customized Action Plan per Month", "1 Intake & Assessment Appointment", "1 Follow-Up Appointment"],
    length: 12
  }
}

export const extractCustomerProductPrice = (tier: CustomerTier) => CustomerProductsMap[tier].price;
export const extractCustomerProductInclusions = (tier: CustomerTier) => CustomerProductsMap[tier].included;
export const extractCustomerProductPriceId = (prod: boolean, tier: CustomerTier) => 
    prod ? CustomerProductsMap[tier].priceId.prod : CustomerProductsMap[tier].priceId.test;
export const isCustomerTier = (value: string): value is CustomerTier => value in CustomerProductsMap;